import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { X, Search } from "lucide-react";

const FIELD = "text-[10px] uppercase tracking-wider text-[#737A76]";

function F({ label, children, span = 1 }) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <Label className={FIELD}>{label}</Label>
      {children}
    </div>
  );
}

const empty = {
  name: "", company: "", address: "", city: "", state: "", zip_code: "",
  email: "", has_email: false, no_email: false,
  primary_only: false, missing_names: false, has_pets: false,
  pet_breed: "", license_plate: "", vehicle_type: "",
  lot_number: "", lease_type: "", contract_status: "",
  check_in_from: "", check_in_to: "", check_out_from: "", check_out_to: "",
  seasonal_or_annual: false, rent_due: false, keyword: "",
};

export default function TenantSearchPanel({ open, onClose, onSearch, onClear }) {
  const [f, setF] = useState(empty);
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });
  const setBool = (k) => (v) => {
    const next = { ...f, [k]: v };
    // has_email & no_email are mutually exclusive
    if (k === "has_email" && v) next.no_email = false;
    if (k === "no_email" && v) next.has_email = false;
    setF(next);
  };

  if (!open) return null;

  const reset = () => { setF(empty); onClear?.(); };
  const submit = () => {
    // Strip blanks before posting
    const body = Object.fromEntries(
      Object.entries(f).filter(([, v]) => v !== "" && v !== false && v != null),
    );
    onSearch(body);
  };

  return (
    <Card className="border-[#EAEAE5] shadow-none mb-4" data-testid="tenant-search-panel">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#737A76]">Find Tenants</div>
            <div className="text-base font-medium tracking-tight" style={{ fontFamily: "Outfit" }}>Multi-field search</div>
          </div>
          <Button size="icon" variant="ghost" onClick={onClose} data-testid="tenant-search-close"><X size={16} /></Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <F label="Name"><Input value={f.name} onChange={set("name")} data-testid="ts-name" /></F>
          <F label="Company / Employer"><Input value={f.company} onChange={set("company")} data-testid="ts-company" /></F>
          <F label="Email"><Input value={f.email} onChange={set("email")} data-testid="ts-email" /></F>
          <F label="License plate"><Input value={f.license_plate} onChange={set("license_plate")} data-testid="ts-lic-plate" /></F>

          <F label="Address" span={2}><Input value={f.address} onChange={set("address")} data-testid="ts-address" /></F>
          <F label="City"><Input value={f.city} onChange={set("city")} data-testid="ts-city" /></F>
          <F label="State"><Input value={f.state} onChange={set("state")} maxLength={2} data-testid="ts-state" /></F>

          <F label="Zip"><Input value={f.zip_code} onChange={set("zip_code")} data-testid="ts-zip" /></F>
          <F label="Lot #"><Input value={f.lot_number} onChange={set("lot_number")} data-testid="ts-lot-number" /></F>
          <F label="Pet breed"><Input value={f.pet_breed} onChange={set("pet_breed")} data-testid="ts-pet-breed" /></F>
          <F label="Vehicle type">
            <select value={f.vehicle_type} onChange={set("vehicle_type")} className="w-full h-10 border border-[#EAEAE5] rounded-md px-3 bg-white text-sm" data-testid="ts-vehicle-type">
              <option value="">Any</option>
              <option>RV</option><option>Car</option><option>Truck</option>
              <option>Trailer</option><option>Motorcycle</option>
            </select>
          </F>

          <F label="Contract status">
            <select value={f.contract_status} onChange={set("contract_status")} className="w-full h-10 border border-[#EAEAE5] rounded-md px-3 bg-white text-sm" data-testid="ts-contract-status">
              <option value="">Any</option>
              <option>Active</option><option>Reservation</option><option>Closed</option><option>Cancelled</option>
            </select>
          </F>
          <F label="Lease type">
            <select value={f.lease_type} onChange={set("lease_type")} className="w-full h-10 border border-[#EAEAE5] rounded-md px-3 bg-white text-sm" data-testid="ts-lease-type">
              <option value="">Any</option>
              <option>Short Term</option><option>Long Term</option><option>Annual</option>
              <option>Seasonal</option><option>Sub Rental</option><option>Group</option>
            </select>
          </F>
          <F label="Check-in from"><Input type="date" value={f.check_in_from} onChange={set("check_in_from")} data-testid="ts-checkin-from" /></F>
          <F label="Check-in to"><Input type="date" value={f.check_in_to} onChange={set("check_in_to")} data-testid="ts-checkin-to" /></F>

          <F label="Check-out from"><Input type="date" value={f.check_out_from} onChange={set("check_out_from")} data-testid="ts-checkout-from" /></F>
          <F label="Check-out to"><Input type="date" value={f.check_out_to} onChange={set("check_out_to")} data-testid="ts-checkout-to" /></F>
          <F label="Keyword (notes)" span={2}><Input value={f.keyword} onChange={set("keyword")} data-testid="ts-keyword" /></F>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-[#F0F0EC]">
          <ToggleRow label="Has email" value={f.has_email} onChange={setBool("has_email")} testId="ts-has-email" />
          <ToggleRow label="No email" value={f.no_email} onChange={setBool("no_email")} testId="ts-no-email" />
          <ToggleRow label="Primary biller only" value={f.primary_only} onChange={setBool("primary_only")} testId="ts-primary" />
          <ToggleRow label="Missing names" value={f.missing_names} onChange={setBool("missing_names")} testId="ts-missing-names" />
          <ToggleRow label="Has pets" value={f.has_pets} onChange={setBool("has_pets")} testId="ts-has-pets" />
          <ToggleRow label="Seasonal / Annual" value={f.seasonal_or_annual} onChange={setBool("seasonal_or_annual")} testId="ts-seasonal" />
          <ToggleRow label="Rent due (open balance)" value={f.rent_due} onChange={setBool("rent_due")} testId="ts-rent-due" />
        </div>

        <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t border-[#F0F0EC]">
          <Button variant="ghost" onClick={reset} data-testid="tenant-search-reset">Reset</Button>
          <Button className="bg-[#284B3B] hover:bg-[#1E3F32] text-white gap-1.5" onClick={submit} data-testid="tenant-search-submit">
            <Search size={14} /> Find
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ToggleRow({ label, value, onChange, testId }) {
  return (
    <label className="flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-[#FAFAF7] border border-[#EAEAE5] cursor-pointer">
      <span className="text-sm text-[#1A211D]">{label}</span>
      <Switch checked={value} onCheckedChange={onChange} data-testid={testId} />
    </label>
  );
}
